'use client';

import { api } from '@/lib/api';

/**
 * 前台埋点公共工具（v2）：
 * - getSessionId(): 匿名访客标识（localStorage 持久化）
 * - getDevice(): 设备类型（供 page-view 上报）
 * - getUiLang(): 用户界面语言（zh-Hant / en，从 AiLumix-store 读取）
 * - trackEvent(event, target?, meta?): 通用行为事件（搜索/看币详情/创建预警等）
 * 所有上报均 fire-and-forget，失败绝不阻断业务。
 */

export function getSessionId(): string {
  try {
    let sid = localStorage.getItem('ailumix-sid') || '';
    if (!sid) {
      // localStorage 不可用/丢失时（App WebView 冷启动），用稳定的匿名设备指纹，
      // 保证同设备访客去重恒定，不再每次启动生成新 ID。
      sid = deviceFingerprint();
      localStorage.setItem('ailumix-sid', sid);
    }
    return sid;
  } catch {
    return 'anon';
  }
}

export function getDevice(): 'desktop' | 'mobile' | 'tablet' {
  if (typeof navigator === 'undefined') return 'desktop';
  const s = navigator.userAgent.toLowerCase();
  if (s.includes('ipad') || s.includes('tablet')) return 'tablet';
  if (s.includes('mobi') || s.includes('android') || s.includes('iphone') || s.includes('ipod')) return 'mobile';
  return 'desktop';
}

/**
 * 匿名设备指纹（djb2 哈希，不落存储、确定性）：
 * App WebView 在部分设备上每次冷启动会丢失 localStorage，导致 sessionId 每次重新生成、
 * 后台「访客」被放大为启动次数。指纹由 UA + 视口 + 语言 + 时区组成，同一台设备恒定，
 * 仅在 localStorage 缺失时用作 sid 兜底；不采集可识别身份的信息。
 */
function deviceFingerprint(): string {
  try {
    const ua = navigator.userAgent || '';
    const scr =
      typeof window !== 'undefined'
        ? `${window.screen.width}x${window.screen.height}x${window.devicePixelRatio || 1}`
        : 'x';
    const lang = navigator.language || '';
    const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone as string | undefined) || '';
    let h = 5381;
    const s = [ua, scr, lang, tz].join('|');
    for (let i = 0; i < s.length; i++) {
      h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    }
    return 'fp' + h.toString(36);
  } catch {
    return 'anon';
  }
}

export function getUiLang(): 'en' | 'zh-Hant' | 'zh-Hans' {
  try {
    const raw = localStorage.getItem('AiLumix-store');
    if (raw) {
      const parsed = JSON.parse(raw) as { state?: { language?: string } };
      if (parsed?.state?.language === 'en' || parsed?.state?.language === 'zh-Hant' || parsed?.state?.language === 'zh-Hans') {
        return parsed.state.language as 'en' | 'zh-Hant' | 'zh-Hans';
      }
    }
  } catch {
    /* ignore */
  }
  return 'en';
}

/** 跨站来源（不同源才上报；同站导航视为直接访问，供后台「来源渠道」分析）。 */
export function getReferrer(): string | null {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    try {
      const a = new URL(ref);
      if (a.hostname === window.location.hostname) return null;
      return ref.slice(0, 255);
    } catch {
      return null;
    }
  } catch {
    return null;
  }
}

export function trackEvent(
  event: string,
  target?: string,
  meta?: Record<string, unknown>,
): void {
  try {
    void api
      .post('/track/event', {
        event,
        target: target ?? null,
        meta: meta ?? null,
        sessionId: getSessionId(),
      })
      .catch(() => {
        /* fire-and-forget */
      });
  } catch {
    /* ignore */
  }
}
